import dayjs from "dayjs";
import { useTourMode } from "../../../../contexts/TourModeContext";
import type { IncomeStatementRecord } from "./useCompanyIncomeStatement";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "../../../../singleton/axios";

export interface CompanyRevenuePoint {
  date: string;
  periodStart: string | null;
  fiscalYear: number;
  period: string;
  currency: string | null;
  basis: string | null;
  value: number | null;
  reason: string | null;
  growth: number | null;
  growthReason: string | null;
}

export interface CompanyRevenue {
  symbol: string;
  version: number;
  revenueVersion: number;
  validUntil: string;
  serverNow: string;
  status: "available" | "unavailable";
  points: CompanyRevenuePoint[];
  cagr3y: number | null;
  cagr5y: number | null;
}

export const useCompanyRevenue = (
  ticker: string,
  limit: number,
  period: "annual" | "quarter" | "ttm",
) => {
  const { isTourMode, getMockData } = useTourMode();
  const tourData = useMemo<CompanyRevenue | undefined>(() => {
    if (!isTourMode) return undefined;
    const rows =
      period === "quarter"
        ? (getMockData<IncomeStatementRecord[]>("incomeStatement") ?? [])
        : [];
    return {
      symbol: ticker,
      version: 0,
      revenueVersion: 0,
      status: "available",
      validUntil: dayjs().add(1, "day").toISOString(),
      serverNow: dayjs().toISOString(),
      cagr3y: null,
      cagr5y: null,
      points: rows
        .slice(0, limit)
        .map((row) => ({
          date: row.date,
          periodStart: null,
          fiscalYear: dayjs(row.date).year(),
          period: `Q${Math.floor(dayjs(row.date).month() / 3) + 1}`,
          currency: "USD",
          basis: "tour",
          value: row.revenueActual,
          reason: null,
          growth: null,
          growthReason: null,
        })),
    };
  }, [getMockData, isTourMode, limit, period, ticker]);
  const query = useQuery<CompanyRevenue, Error>({
    queryKey: ["company-revenue", ticker, period, limit],
    queryFn: async ({ signal }) => {
      const { data } = await api.get<CompanyRevenue>("/company-revenue", {
        params: { ticker, period, limit },
        signal,
      });
      return data;
    },
    enabled: !isTourMode && Boolean(ticker),
    staleTime: 60_000,
    retry: 1,
  });
  return {
    ...query,
    data: isTourMode ? tourData : query.data,
    isLoading: !isTourMode && query.isPending,
  };
};
